import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Empty,
  Descriptions,
  Tag,
  Image,
  Timeline,
} from 'antd';
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ShopOutlined,
  DollarOutlined,
  FileTextOutlined,
  DownloadOutlined,
  PictureOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useWisecureQuery, useWisecureClient } from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { CopyIdButton } from '@/components/CopyIdButton';
import { WisecureCard } from '@/components/base';
import { neutral, primary, success, error, warning, info } from '@/theme';
import type {
  InterventionDetailResponse,
  InterventionEventLogItem,
  CurrencyAmount,
} from '@wisecure/api-client';

const { Text, Title } = Typography;

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: warning[300],
  WAITING_FOR_AGENT_ACCEPTATION: warning[400],
  ACCEPTED: info[400],
  PAYMENT_IN_PROGRESS: info[300],
  PAYMENT_DONE: success[400],
  PAYMENT_FAILED: error[400],
  PAYMENT_TIMED_OUT: error[300],
  AGENT_MOVING: primary[300],
  INSPECTION_IN_PROGRESS: primary[400],
  NO_EFFRACTION: success[300],
  EFFRACTION: error[400],
  NO_SURVEILLANCE: neutral[300],
  SURVEILLANCE_IN_PROGRESS: info[400],
  SURVEILLANCE_DONE: success[400],
  REPORT_IN_PROGRESS: primary[300],
  DONE: success[500],
  CANCELED: neutral[300],
  REJECTED: error[400],
  HIDDEN: neutral[200],
  PRICE_PROPOSED: warning[300],
  PRICE_ACCEPTED: success[300],
};

const STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Demandé',
  WAITING_FOR_AGENT_ACCEPTATION: 'En attente d\'acceptation',
  ACCEPTED: 'Accepté',
  PAYMENT_IN_PROGRESS: 'Paiement en cours',
  PAYMENT_DONE: 'Paiement effectué',
  PAYMENT_FAILED: 'Paiement échoué',
  PAYMENT_TIMED_OUT: 'Paiement expiré',
  AGENT_MOVING: 'Agent en déplacement',
  INSPECTION_IN_PROGRESS: 'Inspection en cours',
  NO_EFFRACTION: 'Pas d\'effraction',
  EFFRACTION: 'Effraction',
  NO_SURVEILLANCE: 'Pas de surveillance',
  SURVEILLANCE_IN_PROGRESS: 'Surveillance en cours',
  SURVEILLANCE_DONE: 'Surveillance terminée',
  REPORT_IN_PROGRESS: 'Rapport en cours',
  DONE: 'Terminé',
  CANCELED: 'Annulé',
  REJECTED: 'Refusé',
  HIDDEN: 'Masqué',
  PRICE_PROPOSED: 'Prix proposé',
  PRICE_ACCEPTED: 'Prix accepté',
};

const EVENT_LABELS: Record<string, string> = {
  assigned: 'Agent assigné',
  accepted: 'Intervention acceptée',
  position: 'Position mise à jour',
  arrived: 'Agent arrivé',
  step: 'Étape mise à jour',
  effraction: 'Effraction déclarée',
  completed: 'Intervention terminée',
  cancelled: 'Intervention annulée',
  movement_timeout: 'Timeout de déplacement',
  rejected: 'Intervention refusée',
  offered: 'Intervention proposée',
  offer_revoked: 'Offre révoquée',
  countdown_started: 'Compte à rebours démarré',
  countdown_expired: 'Compte à rebours expiré',
  payment: 'Paiement',
  surveillance: 'Surveillance',
  report_ready: 'Rapport prêt',
  price_proposed: 'Prix proposé',
  price_accepted: 'Prix accepté',
  price_rejected: 'Prix rejeté',
  price_expired: 'Prix expiré',
};

const EVENT_COLORS: Record<string, string> = {
  assigned: info[400],
  accepted: success[400],
  position: neutral[300],
  arrived: success[300],
  step: primary[400],
  effraction: error[400],
  completed: success[500],
  cancelled: neutral[300],
  movement_timeout: warning[400],
  rejected: error[400],
  offered: info[300],
  offer_revoked: warning[300],
  countdown_started: info[300],
  countdown_expired: warning[400],
  payment: success[400],
  surveillance: info[400],
  report_ready: primary[400],
  price_proposed: warning[300],
  price_accepted: success[300],
  price_rejected: error[300],
  price_expired: warning[400],
};

function formatPrice(amount?: CurrencyAmount): string {
  if (!amount) return '—';
  return `${amount.amount.toFixed(2)} ${amount.symbol}`;
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '—';
  const mins = Math.round(seconds / 60);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h${m > 0 ? ` ${m}min` : ''}`;
  }
  return `${mins} min`;
}

function formatPhone(phone?: { countryCode?: unknown; number?: unknown; fullNumber?: unknown }): string {
  if (!phone) return '—';
  if (phone.fullNumber) return String(phone.fullNumber);
  if (phone.number) return String(phone.number);
  return '—';
}

interface PhotosResponse {
  reportId?: string | null;
  photos?: Array<{ id?: string; url?: string; caption?: string | null; createdAt?: string }>;
}

export function InterventionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useWisecureClient();

  const { data: intervention, isLoading } = useWisecureQuery<InterventionDetailResponse>(
    queryKeys.interventionDetail(id!),
    (c) => c.interventions.interventionsControllerFindOne({ id: id! }),
    { enabled: !!id },
  );

  const { data: eventLog, isLoading: eventsLoading } = useWisecureQuery<InterventionEventLogItem[]>(
    queryKeys.interventionEventLog(id!),
    (c) => c.interventions.interventionsControllerGetEventLog({ id: id! }),
    { enabled: !!id },
  );

  const { data: photosData, isLoading: photosLoading } = useWisecureQuery<PhotosResponse>(
    queryKeys.interventionPhotos(id!),
    (c) => c.interventions.interventionsControllerGetPhotos({ id: id! }) as Promise<PhotosResponse>,
    { enabled: !!id },
  );

  const handleDownloadReportPdf = async () => {
    if (!id) return;
    try {
      const result = await client.interventions.interventionsControllerGetReportPdfUrl({ id });
      if (result.url) window.open(result.url, '_blank');
    } catch { /* noop */ }
  };

  const handleDownloadInvoicePdf = async () => {
    if (!id) return;
    try {
      const result = await client.interventions.interventionsControllerGetInvoicePdfUrl({ id });
      if (result.url) window.open(result.url, '_blank');
    } catch { /* noop */ }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!intervention) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Empty description="Intervention non trouvée">
          <Button onClick={() => navigate('/interventions')}>Retour à la liste</Button>
        </Empty>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[intervention.status] ?? neutral[300];
  const statusLabel = STATUS_LABELS[intervention.status] ?? intervention.status;
  const clientName = intervention.client
    ? [intervention.client.clientProfile?.firstName, intervention.client.clientProfile?.lastName].filter(Boolean).join(' ')
    : '—';
  const agentName = intervention.agent
    ? [intervention.agent.agentProfile?.firstName, intervention.agent.agentProfile?.lastName].filter(Boolean).join(' ')
    : '—';
  const photos = photosData?.photos ?? [];
  const canCancel = !['DONE', 'CANCELED', 'REJECTED', 'HIDDEN'].includes(intervention.status);

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ color: neutral[500] }} />
        <Text style={{ color: neutral[300], fontSize: 13 }}>Interventions</Text>
        <Text style={{ color: neutral[200] }}>/</Text>
        <Text style={{ color: neutral[700], fontWeight: 500, fontSize: 13 }}>{id?.slice(0, 8)}...</Text>
      </div>

      {/* Header Card */}
      <WisecureCard dsVariant="highlight" style={{ marginBottom: 24, padding: 0 }} styles={{ body: { padding: '28px 32px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: statusColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <EnvironmentOutlined style={{ fontSize: 24, color: neutral[0] }} />
            </div>
            <div>
              <Title level={3} style={{ margin: 0, color: neutral[0], fontWeight: 700, letterSpacing: '-0.5px' }}>
                Intervention
              </Title>
              <Space size={8} style={{ marginTop: 6 }} wrap>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: `${statusColor}20`,
                    color: statusColor,
                    borderRadius: 100,
                    padding: '3px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {statusLabel}
                </div>
                {intervention.hasOffense && (
                  <Tag color="error" style={{ borderRadius: 100 }}>
                    <ExclamationCircleOutlined /> Effraction
                  </Tag>
                )}
              </Space>
            </div>
          </div>

          <Space size={8}>
            <CopyIdButton id={intervention.id} type="primary" ghost size="small" />
          </Space>
        </div>

        <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '20px 0 16px' }} />

        <Row gutter={24}>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Créé le</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {new Date(intervention.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Début</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {intervention.startedAt
                  ? new Date(intervention.startedAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Fin</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {intervention.completedAt
                  ? new Date(intervention.completedAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Durée</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {formatDuration(intervention.durationSeconds)}
              </Text>
            </div>
          </Col>
        </Row>
      </WisecureCard>

      <Row gutter={24}>
        {/* Left Column */}
        <Col xs={24} lg={14}>
          {/* Client Info */}
          <WisecureCard
            title={
              <Space>
                <UserOutlined style={{ color: primary[400] }} />
                <span>Client</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nom">{clientName}</Descriptions.Item>
              <Descriptions.Item label={<><MailOutlined style={{ marginRight: 6 }} />Email</>}>
                {intervention.client?.email ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 6 }} />Téléphone</>}>
                {formatPhone(intervention.client?.clientProfile?.phone)}
              </Descriptions.Item>
              <Descriptions.Item label={<><HomeOutlined style={{ marginRight: 6 }} />Adresse</>}>
                {intervention.locationAddress}
              </Descriptions.Item>
            </Descriptions>
          </WisecureCard>

          {/* Agent Info */}
          <WisecureCard
            title={
              <Space>
                <ShopOutlined style={{ color: primary[400] }} />
                <span>Agent</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nom">{agentName}</Descriptions.Item>
              <Descriptions.Item label={<><MailOutlined style={{ marginRight: 6 }} />Email</>}>
                {intervention.agent?.email ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 6 }} />Téléphone</>}>
                {formatPhone(intervention.agent?.agentProfile?.phone)}
              </Descriptions.Item>
              <Descriptions.Item label="Société">
                <Tag style={{ borderRadius: 100 }}>{intervention.society?.name ?? '—'}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </WisecureCard>

          {/* Intervention Details */}
          <WisecureCard
            title={
              <Space>
                <EnvironmentOutlined style={{ color: primary[400] }} />
                <span>Détails de l'intervention</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Adresse">{intervention.locationAddress}</Descriptions.Item>
              <Descriptions.Item label="Coordonnées">
                <a
                  href={`https://www.google.com/maps?q=${intervention.locationLatitude},${intervention.locationLongitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: primary[400] }}
                >
                  {intervention.locationLatitude.toFixed(6)}, {intervention.locationLongitude.toFixed(6)}
                </a>
              </Descriptions.Item>
              {intervention.location?.label && (
                <Descriptions.Item label="Lieu">{intervention.location.label}</Descriptions.Item>
              )}
              {intervention.sector && (
                <Descriptions.Item label="Secteur">{intervention.sector.label ?? intervention.sectorId}</Descriptions.Item>
              )}
            </Descriptions>

            {intervention.pricingData && (
              <>
                <Divider orientation="left" style={{ margin: '16px 0 12px' }}>
                  <Space>
                    <DollarOutlined />
                    <span>Tarification</span>
                  </Space>
                </Divider>
                <Descriptions column={2} size="small">
                  {intervention.pricingData.interventionPrice != null && (
                    <Descriptions.Item label="Prix de base">
                      {intervention.pricingData.interventionPrice.toFixed(2)} EUR
                    </Descriptions.Item>
                  )}
                  {intervention.pricingData.extraTimePrice != null && (
                    <Descriptions.Item label="Prix temps supplémentaire">
                      {intervention.pricingData.extraTimePrice.toFixed(2)} EUR/h
                    </Descriptions.Item>
                  )}
                  {intervention.pricingData.extraDistancePrice != null && (
                    <Descriptions.Item label="Prix distance supplémentaire">
                      {intervention.pricingData.extraDistancePrice.toFixed(2)} EUR/km
                    </Descriptions.Item>
                  )}
                  {intervention.pricingData.subtotal != null && (
                    <Descriptions.Item label="Sous-total HT">
                      {intervention.pricingData.subtotal.toFixed(2)} EUR
                    </Descriptions.Item>
                  )}
                  {intervention.pricingData.tva != null && (
                    <Descriptions.Item label="TVA">
                      {intervention.pricingData.tva.toFixed(2)} EUR
                    </Descriptions.Item>
                  )}
                  {intervention.pricingData.clientTotalTtc != null && (
                    <Descriptions.Item label="Total client TTC">
                      <Text strong>{intervention.pricingData.clientTotalTtc.toFixed(2)} EUR</Text>
                    </Descriptions.Item>
                  )}
                  {intervention.pricingData.netToSociety != null && (
                    <Descriptions.Item label="Net société">
                      {intervention.pricingData.netToSociety.toFixed(2)} EUR
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            {(intervention.agreedTotalAmount || intervention.proposedPrice) && (
              <div
                style={{
                  background: `${success[400]}08`,
                  border: `1px solid ${success[400]}20`,
                  borderRadius: 10,
                  padding: '12px 16px',
                  marginTop: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <DollarOutlined style={{ color: success[400], fontSize: 18 }} />
                <div>
                  <Text style={{ fontSize: 12, color: neutral[300], display: 'block' }}>
                    {intervention.agreedTotalAmount ? 'Montant convenu' : 'Prix proposé'}
                  </Text>
                  <Text style={{ fontWeight: 700, fontSize: 18, color: success[400] }}>
                    {formatPrice(intervention.agreedTotalAmount ?? intervention.proposedPrice)}
                  </Text>
                </div>
              </div>
            )}
          </WisecureCard>

          {/* Report Section */}
          {intervention.report && (
            <WisecureCard
              title={
                <Space>
                  <FileTextOutlined style={{ color: primary[400] }} />
                  <span>Rapport</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              {intervention.report.notes && (
                <div style={{ marginBottom: 16 }}>
                  <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Notes de l'agent</Text>
                  <Text>{intervention.report.notes}</Text>
                </div>
              )}
              {intervention.report.pdfStoragePath && (
                <Button icon={<DownloadOutlined />} onClick={handleDownloadReportPdf} block>
                  Télécharger le rapport PDF
                </Button>
              )}
            </WisecureCard>
          )}

          {/* Photos Grid */}
          <WisecureCard
            title={
              <Space>
                <PictureOutlined style={{ color: primary[400] }} />
                <span>Photos ({photos.length})</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {photosLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
            ) : photos.length > 0 ? (
              <Image.PreviewGroup>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {photos.map((photo) => (
                    <Image
                      key={photo.id}
                      src={photo.url}
                      alt={photo.caption ?? 'Photo'}
                      width={120}
                      height={120}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F/PQAJhAN4f2hM2AAAAABJRU5ErkJggg=="
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            ) : (
              <Text style={{ color: neutral[300] }}>Aucune photo disponible</Text>
            )}
          </WisecureCard>
        </Col>

        {/* Right Column */}
        <Col xs={24} lg={10}>
          {/* Actions Panel */}
          <WisecureCard
            title={
              <Space>
                <CheckCircleOutlined style={{ color: primary[400] }} />
                <span>Actions</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              {canCancel ? (
                <Button
                  danger
                  icon={<StopOutlined />}
                  block
                  style={{ height: 44, fontWeight: 600 }}
                  disabled
                >
                  Annuler l'intervention
                </Button>
              ) : (
                <div
                  style={{
                    background: `${neutral[300]}08`,
                    border: `1px solid ${neutral[300]}20`,
                    borderRadius: 10,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <CloseCircleOutlined style={{ color: neutral[300], fontSize: 16 }} />
                  <Text style={{ color: neutral[300], fontSize: 13 }}>
                    Aucune action disponible pour ce statut
                  </Text>
                </div>
              )}
              <Button
                icon={<DollarOutlined />}
                block
                style={{ height: 44, fontWeight: 600 }}
                disabled
              >
                Remboursement
              </Button>
              {intervention.invoice?.pdfStoragePath && (
                <Button
                  icon={<DownloadOutlined />}
                  block
                  onClick={handleDownloadInvoicePdf}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Télécharger la facture
                </Button>
              )}
            </Space>
          </WisecureCard>

          {/* Invoice Info */}
          {intervention.invoice && (
            <WisecureCard
              title={
                <Space>
                  <FileTextOutlined style={{ color: primary[400] }} />
                  <span>Facture</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Numéro">{intervention.invoice.invoiceNumber}</Descriptions.Item>
                <Descriptions.Item label="Statut">
                  <Tag
                    color={intervention.invoice.status === 'PAID' ? 'success' : intervention.invoice.status === 'VOID' ? 'error' : 'warning'}
                    style={{ borderRadius: 100 }}
                  >
                    {intervention.invoice.status}
                  </Tag>
                </Descriptions.Item>
                {intervention.invoice.totalTtc != null && (
                  <Descriptions.Item label="Total TTC">{intervention.invoice.totalTtc.toFixed(2)} EUR</Descriptions.Item>
                )}
                <Descriptions.Item label="Émise le">
                  {new Date(intervention.invoice.issuedAt).toLocaleDateString('fr-FR')}
                </Descriptions.Item>
                {intervention.invoice.paidAt && (
                  <Descriptions.Item label="Payée le">
                    {new Date(intervention.invoice.paidAt).toLocaleDateString('fr-FR')}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </WisecureCard>
          )}

          {/* Timeline */}
          <WisecureCard
            title={
              <Space>
                <ClockCircleOutlined style={{ color: primary[400] }} />
                <span>Chronologie</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {eventsLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
            ) : eventLog && eventLog.length > 0 ? (
              <Timeline
                items={eventLog
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((event) => ({
                    color: EVENT_COLORS[event.eventType] ?? neutral[300],
                    children: (
                      <div>
                        <Text style={{ fontWeight: 600, fontSize: 13 }}>
                          {EVENT_LABELS[event.eventType] ?? event.eventType}
                        </Text>
                        <br />
                        <Text style={{ color: neutral[300], fontSize: 12 }}>
                          {new Date(event.createdAt).toLocaleString('fr-FR')}
                        </Text>
                      </div>
                    ),
                  }))}
              />
            ) : (
              <Text style={{ color: neutral[300] }}>Aucun événement</Text>
            )}
          </WisecureCard>
        </Col>
      </Row>
    </div>
  );
}
